import React, {
  DetailedHTMLProps,
  InputHTMLAttributes,
  forwardRef,
  useMemo,
} from 'react';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps } from '@react-aria/utils';
import classnames from 'classnames';
import { CheckIcon, MinusIcon } from '@stpkg/stand-react';

export type RSCheckBoxProps = DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> & {
  isIndeterminate?: boolean;
  isError?: boolean;
};

export const RSCheckBox = forwardRef<HTMLInputElement, RSCheckBoxProps>(
  ({ isIndeterminate, isError, ...props }, ref) => {
    const { disabled, readOnly } = props;
    const isDisabled = useMemo(() => {
      return disabled || readOnly;
    }, [disabled, readOnly]);

    const { isFocusVisible, focusProps } = useFocusRing();

    const color = '#0066FF';

    const borderColorClasses = useMemo(() => {
      if (isDisabled) {
        if (isIndeterminate) {
          return ['border-gray-200'];
        } else {
          return ['border-gray-50', 'peer-checked:border-gray-200'];
        }
      }

      if (isError) {
        return ['border-red-700'];
      }

      return ['border-gray-200'];
    }, [isIndeterminate, isError, isDisabled]);

    const bgColorClasses = useMemo(() => {
      if (isDisabled) {
        if (isIndeterminate) {
          return ['bg-gray-200'];
        } else {
          return ['bg-gray-10', 'peer-checked:bg-gray-200'];
        }
      }

      if (isError) {
        if (isIndeterminate) {
          return ['bg-red-700'];
        } else {
          return ['bg-red-10', 'peer-checked:bg-red-700'];
        }
      }

      return ['bg-white'];
    }, [isIndeterminate, isError, isDisabled]);

    return (
      <>
        <div
          className={classnames(
            'wrapper',
            isFocusVisible && 'focus-visible',
            'inline-block',
            'relative',
            'w-[20px]',
            'h-[20px]',
            'after:transition',
          )}
        >
          <input
            {...mergeProps(props, focusProps)}
            ref={ref}
            type="checkbox"
            className={classnames(
              'input',
              'appearance-none',
              'absolute',
              'w-full',
              'h-full',
              'z-1',
              'peer',
              !isDisabled ? 'cursor-pointer' : 'cursor-not-allowed',
              'outline-none',
              'focus:outline-none',
            )}
          />
          <span
            className={classnames(
              'mark',
              !isError && !isDisabled && 'normality',
              isIndeterminate && 'indeterminate',
              'box-border',
              'inline-flex',
              'justify-center',
              'items-center',
              'w-full',
              'h-full',
              bgColorClasses,
              'border-solid',
              'border-2',
              borderColorClasses,
              'rounded-5',
              'transition',
            )}
          >
            <div className="icon-wrapper invisible h-16 w-16 transition">
              {isIndeterminate ? (
                <MinusIcon
                  className="block fill-white"
                  width="16"
                  height="16"
                  viewBox="0 0 48 48"
                />
              ) : (
                <CheckIcon
                  className="block fill-white"
                  width="16"
                  height="16"
                  viewBox="0 0 48 48"
                />
              )}
            </div>
          </span>
        </div>
        <style jsx>{`
          .wrapper::after {
            content: '';
            box-sizing: border-box;
            display: block;
            position: absolute;
            width: calc(100% + 8px);
            height: calc(100% + 8px);
            top: -4px;
            left: -4px;
            border-style: solid;
            border-width: 2px;
            border-color: transparent;
            border-radius: 7px;
          }

          .wrapper.focus-visible::after {
            border-color: ${color};
          }

          .input:checked + .mark.normality,
          .input + .mark.normality.indeterminate {
            border-color: ${color};
          }

          .input + .mark.normality.indeterminate,
          .input:checked + .mark.normality {
            background-color: ${color};
          }

          .input + .mark.indeterminate .icon-wrapper,
          .input:checked + .mark .icon-wrapper {
            visibility: visible;
          }
        `}</style>
      </>
    );
  },
);

RSCheckBox.displayName = 'RSCheckBox';
